import { toast } from '@iguanads/react'
import { createContext, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'

import { OrderStatusEnum } from '@/enums/order.enum'
import { Permission } from '@/enums/permission.enum'
import { useRealtimeOrders } from '@/hooks/use-realtime-orders'
import useAudio from '@/hooks/useAudio'
import useAuth from '@/hooks/useAuth'
import type { OrderRealtimeChunkType } from '@/types/order-realtime.types'
import { isOrderOnGoing } from '@/utils/functions/order'

type LiveOrdersContextProps = {
  orders: OrderRealtimeChunkType[]
  ongoing: OrderRealtimeChunkType[]
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void
  setOrders: React.Dispatch<React.SetStateAction<OrderRealtimeChunkType[]>>
  isDismissItemsNotAvailableModal: boolean
  setIsDismissItemsNotAvailableModal: (isDismiss: boolean) => void
}

const LiveOrdersContext = createContext<LiveOrdersContextProps>(
  {} as LiveOrdersContextProps
)

type LiveOrdersProviderProps = {
  children: React.ReactNode
}

const status = [
  OrderStatusEnum.CREATED,
  OrderStatusEnum.IN_PROGRESS,
  OrderStatusEnum.READY,
  OrderStatusEnum.COLLECTED,
  OrderStatusEnum.IN_TRANSIT,
  OrderStatusEnum.SCHEDULED,
]

function LiveOrdersProvider({ children }: LiveOrdersProviderProps) {
  const [ongoing, setOngoing] = useState<OrderRealtimeChunkType[]>([])
  const [isLoadingOverride, setIsLoading] = useState(false)
  const { merchantsSelected, isSuperAdmin, user } = useAuth()
  const { play, playing, canPlay, pause } = useAudio()
  const { pathname } = useLocation()
  const { id } = useParams()
  const navigate = useNavigate()
  const [isShowingNewOrder, setIsShowingNewOrder] = useState(false)
  const newOrderToastRef = useRef<string | number | null>(null)
  const [dismissedCreatedOrderIds, setDismissedCreatedOrderIds] = useState<
    string[]
  >([])
  const [isDismissItemsNotAvailableModal, setIsDismissItemsNotAvailableModal] =
    useState(false)

  const merchantIds = merchantsSelected.map((merchant) => merchant.id)

  const { orders, isLoading: isRealtimeLoading, setOrders } = useRealtimeOrders({
    merchantIds,
    statuses: status,
    enabled: merchantIds.length > 0,
  })

  const isLoading = isLoadingOverride || isRealtimeLoading

  const paramOrderId = id

  useEffect(() => {
    if (isSuperAdmin) return

    const isNotAllowedPage =
      pathname === '/' ||
      pathname === '/dashboard' ||
      pathname === `/shopper/${paramOrderId}`

    if (isNotAllowedPage) return

    const createdOrderIds = orders
      .filter((order) => order.lastStatus === 'created')
      .map((order) => order.orderId)
    const hasNewOrders = createdOrderIds.length > 0
    const hasUndismissedNewOrders = createdOrderIds.some(
      (orderId) => !dismissedCreatedOrderIds.includes(orderId)
    )

    if (!hasNewOrders && playing) {
      pause()
    }

    if (!hasNewOrders && dismissedCreatedOrderIds.length > 0) {
      setDismissedCreatedOrderIds([])
    }

    if (
      hasNewOrders &&
      hasUndismissedNewOrders &&
      canPlay &&
      !isNotAllowedPage &&
      !isShowingNewOrder &&
      isDismissItemsNotAvailableModal &&
      (user?.permissions.includes(Permission.DASHBOARD) ||
        user?.permissions.includes(Permission.LIVE_ORDERS))
    ) {
      play()
      setIsShowingNewOrder(true)

      newOrderToastRef.current = toast(
        {
          type: 'info',
          title: 'New order!',
          message: 'Go to Live tab to accept this order.',
          hideCloseButton: false,
          actionButton: false,
          actionButtonText: 'Go to live orders',
          onClickActionButton: () => {
            if (newOrderToastRef.current) {
              toast.dismiss(newOrderToastRef.current)
            }
            setDismissedCreatedOrderIds((prev) =>
              Array.from(new Set([...prev, ...createdOrderIds]))
            )
            setIsShowingNewOrder(false)
            navigate('/')
          },
        },
        {
          onClose: () => {
            setDismissedCreatedOrderIds((prev) =>
              Array.from(new Set([...prev, ...createdOrderIds]))
            )
            setIsShowingNewOrder(false)
            newOrderToastRef.current = null
            pause()
          },
          autoClose: 600000,
        }
      )
    }
  }, [
    canPlay,
    dismissedCreatedOrderIds,
    isShowingNewOrder,
    isSuperAdmin,
    navigate,
    orders,
    pathname,
    paramOrderId,
    isDismissItemsNotAvailableModal,
    pause,
    play,
    playing,
    user?.permissions,
  ])

  useEffect(() => {
    setOngoing(orders.filter((order) => isOrderOnGoing(order.lastStatus)))
  }, [orders])

  return (
    <LiveOrdersContext.Provider
      value={{
        orders,
        ongoing,
        isLoading,
        setIsLoading,
        setOrders,
        isDismissItemsNotAvailableModal,
        setIsDismissItemsNotAvailableModal,
      }}
    >
      {children}
    </LiveOrdersContext.Provider>
  )
}

export { LiveOrdersContext, LiveOrdersProvider }
